/* ⚠️ GENERATED from app/services/avatarAssemble.ts by gen-twins.mjs — edit the TypeScript, not this. */
// services/avatarAssemble.ts — a spec and its catalogue parts become one rigged figure.
//
// 🟥 EVERY PART IS ITS OWN .glb WITH ITS OWN COPY OF THE SKELETON. Drawn as
//    loaded, a wave would move the body and leave the shirt standing. The
//    assembler mounts every part's skinned mesh onto the BODY's bones — the
//    same Bone objects, matched by name — so the scene's motion code moves
//    one skeleton and everything follows. The part's own armature is dropped.
//
// 🟢 CLONES, NOT THE CACHE. Parsed parts are shared by every avatar that uses
//    them (src/avatar/kitCache.ts); an assembly clones what it mounts and
//    gives each mesh its own material, so painting one avatar's shirt blue
//    never paints another's. The texture (the skin bake) stays shared.
//
// 🟢 PAINT IS SEPARATE FROM ASSEMBLY. A colour or build change re-paints the
//    figure in place (`applySpec`); only a different part list re-assembles.
//    That is what makes the builder's preview follow a swatch tap at once.
//
// 🟢 THIS FILE ONLY KNOWS `three`. A twin is generated for the web.

import * as THREE from 'three';
import { clone as skeletonClone } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { paintFor, heightScale, partIdsFor, normalizeSpec } from './avatarKit';
import { applyPose } from './avatarPose';
import { faceMorphName } from './avatarFace';
const normBone = n => String(n || '').toLowerCase().replace(/[\s:._]/g, '');
function bonesByName(root) {
  const m = new Map();
  root.traverse(o => {
    if (o.isBone) m.set(normBone(o.name), o);
  });
  return m;
}
function skinnedMeshes(root) {
  const out = [];
  root.traverse(o => {
    if (o.isSkinnedMesh) out.push(o);
  });
  return out;
}

/** The kind a mesh inside the body file is: the skin, or the base layer that is never removed. */
function bodyMeshKind(mesh) {
  return /^base[-_]/i.test(mesh.name) ? 'base' : 'body';
}

/**
 * Mount `sources` (the body first) into one figure. Every skinned mesh of every
 * part is re-bound to the body's bones by name; a part whose bones the body
 * does not have is skipped (and reported through `missing`).
 */
export function assembleAvatar(sources, spec) {
  if (!sources.length || sources[0].part.kind !== 'body') throw new Error('avatar-kit: the body must come first');
  const body = skeletonClone(sources[0].root);
  const bones = bonesByName(body);
  const missing = [];
  for (const m of skinnedMeshes(body)) {
    m.userData.kitKind = bodyMeshKind(m);
    m.material = m.material.clone();
    m.frustumCulled = false;
  }
  for (const src of sources.slice(1)) {
    const c = skeletonClone(src.root);
    for (const m of skinnedMeshes(c)) {
      const own = m.skeleton;
      const mapped = [];
      let ok = true;
      for (const b of own.bones) {
        const bb = bones.get(normBone(b.name));
        if (!bb) {
          ok = false;
          break;
        }
        mapped.push(bb);
      }
      if (!ok) {
        missing.push(src.part.id);
        continue;
      }
      const inverses = own.boneInverses.map(mx => mx.clone());
      m.removeFromParent();
      m.bind(new THREE.Skeleton(mapped, inverses), m.bindMatrix.clone());
      m.userData.kitKind = src.part.kind;
      m.userData.kitId = src.part.id;
      m.material = m.material.clone();
      m.frustumCulled = false;
      body.add(m);
    }
  }
  /* 🟢 the face AND the build, before the pose: baking changes geometry, posing
     changes bones. One pass, because a body proportion is exactly as permanent
     as a nose and the catalogue names both `f_<key>_<lo|hi>` for that reason. */
  const shape = shapeOf(spec);
  if (Object.keys(shape).length) {
    for (const m of skinnedMeshes(body)) {
      if (m.userData.kitKind === 'eyes') bakeEyes(m, shape);else bakeFace(m, shape);
    }
  }
  body.userData.kitFace = shape;
  body.userData.kitPartIds = partIdsFor(spec);
  body.userData.kitMissing = missing;
  applySpec(body, spec);
  /* 🟢 THE POSE IS APPLIED ONCE, TO THIS CLONE. Rotations compound, so a pose is
     never re-applied in place — a different pose is a new assembly (cheap: the
     parts are already parsed and shared; see needsReassembly). */
  body.userData.kitPose = applyPose(body, normalizeSpec(spec).pose);
  return body;
}

/** The part ids an assembled figure was built from (what decides whether a change needs a rebuild). */
export function assembledPartIds(root) {
  return Array.isArray(root?.userData?.kitPartIds) ? root.userData.kitPartIds : [];
}

/** Whether `spec` can be applied to `root` in place, or needs a new assembly. */
export function needsReassembly(root, spec) {
  const have = assembledPartIds(root);
  const want = partIdsFor(spec);
  if (have.length !== want.length || have.some((id, i) => id !== want[i])) return true;
  const s = normalizeSpec(spec);
  /* a baked face cannot be un-baked in place, and a pose cannot be re-applied */
  if (JSON.stringify(root?.userData?.kitFace || {}) !== JSON.stringify(shapeOf(s))) return true;
  return root?.userData?.kitPose !== s.pose;
}

/** Paint the figure to the spec: skin tint, part colours, build morphs, height. In place. */
export function applySpec(root, spec) {
  const s = normalizeSpec(spec);
  root.traverse(o => {
    if (!o.isMesh) return;
    const kind = o.userData?.kitKind;
    if (!kind) return;
    const mat = o.material;
    if (kind === 'base') {
      setMorphs(o, {
        slim: Math.max(0, -s.build),
        heavy: Math.max(0, s.build)
      });
      return;
    }
    const paint = paintFor(kind, s);
    if (paint.tint && mat && mat.color) mat.color.setRGB(paint.tint[0], paint.tint[1], paint.tint[2], THREE.LinearSRGBColorSpace);else if (paint.color && mat && mat.color) mat.color.set(paint.color);
    if (mat && kind === 'hair') {
      mat.roughness = 0.62;
      mat.metalness = 0;
    }
    setMorphs(o, paint.morphs);
  });
  const sc = heightScale(s.height);
  root.scale.setScalar(sc);
  root.userData.kitSpecHeight = s.height;
}
function setMorphs(mesh, morphs) {
  const d = mesh.morphTargetDictionary,
    inf = mesh.morphTargetInfluences;
  if (!d || !inf) return;
  for (const k of Object.keys(morphs)) {
    const i = d[k];
    if (typeof i === 'number') inf[i] = morphs[k];
  }
}

/* ─── the face ─────────────────────────────────────────────────────────── */

/**
 * 🟥 THE FACE IS BAKED, NOT LEFT LIVE. Twenty-three dimensions is forty-six
 *    morph targets on the body, and a live morph costs every vertex, every
 *    frame, whether its influence is zero or not. A face never animates — it is
 *    who you are — so it is added into the vertex positions ONCE, here, and the
 *    face targets are then dropped. What stays live is the six that move: the
 *    build pair and the four expressions.
 *
 * 🟥 AND THE CACHED GEOMETRY IS NEVER TOUCHED. Parts are shared by every avatar
 *    wearing them; baking into the shared buffer would give the whole chat list
 *    one person's nose. A new geometry is made that SHARES every attribute it
 *    does not change (uv, skinning, index) and owns only the positions and the
 *    normals it had to recompute.
 */
/**
 * 🟥 `attribute.array` IS NOT THE VERTEX LIST. The catalogue's buffers are
 *    INTERLEAVED (position, normal and uv share one block), so copying `.array`
 *    copies everything and indexing it by `i * 3` reads a normal as a position —
 *    which sends one vertex to 3.4e38 and the head to infinity. Reading through
 *    the attribute is correct for both layouts and costs the same pass.
 */
export function readPositions(p) {
  const out = new Float32Array(p.count * 3);
  for (let i = 0, n = p.count; i < n; i++) {
    out[i * 3] = p.getX(i);
    out[i * 3 + 1] = p.getY(i);
    out[i * 3 + 2] = p.getZ(i);
  }
  return out;
}

/** The face and the build as one vector — what actually gets baked. */
export function shapeOf(spec) {
  const s = normalizeSpec(spec);
  return {
    ...s.face3d,
    ...s.body3d
  };
}
export function bakeFace(mesh, face) {
  const dict = mesh.morphTargetDictionary;
  const src = mesh.geometry;
  if (!dict || !src?.morphAttributes?.position) return;
  const faceNames = Object.keys(dict).filter(n => n.startsWith('f_'));
  if (!faceNames.length) return;
  const deltas = [];
  /* 🟢 the vector's OWN keys, not a fixed list: normalizeFace and normalizeBody
     have already dropped anything the catalogue does not ship, and a part that
     carries only some of them (hair carries five) simply misses the rest. */
  for (const key of Object.keys(face || {})) {
    const v = Number(face?.[key]) || 0;
    if (!v) continue;
    const idx = dict[faceMorphName(key, v)];
    const attr = typeof idx === 'number' ? src.morphAttributes.position[idx] : null;
    if (attr) deltas.push({
      attr,
      weight: Math.abs(v)
    });
  }
  const geo = new THREE.BufferGeometry();
  if (src.index) geo.setIndex(src.index);
  for (const name of Object.keys(src.attributes)) geo.setAttribute(name, src.attributes[name]);
  if (deltas.length) {
    const p = src.attributes.position;
    const arr = readPositions(p);
    for (const {
      attr,
      weight
    } of deltas) {
      for (let i = 0, n = p.count; i < n; i++) {
        arr[i * 3] += attr.getX(i) * weight;
        arr[i * 3 + 1] += attr.getY(i) * weight;
        arr[i * 3 + 2] += attr.getZ(i) * weight;
      }
    }
    geo.setAttribute('position', new THREE.BufferAttribute(arr, 3));
    /* the surface moved, so the shading has to move with it */
    geo.deleteAttribute('normal');
    geo.computeVertexNormals();
  }

  /* keep only the targets that still animate */
  const keepNames = Object.keys(dict).filter(n => !n.startsWith('f_'));
  const keep = keepNames.map(n => src.morphAttributes.position[dict[n]]).filter(Boolean);
  if (keep.length) geo.morphAttributes.position = keep;
  geo.morphTargetsRelative = src.morphTargetsRelative !== false;
  mesh.geometry = geo;
  mesh.morphTargetDictionary = {};
  keepNames.forEach((n, i) => {
    mesh.morphTargetDictionary[n] = i;
  });
  mesh.morphTargetInfluences = new Array(keep.length).fill(0);
  mesh.userData.kitFaceBaked = true;
}

/**
 * The eyeballs are their own mesh with no face morphs, so the two dimensions
 * that move the socket move them too — otherwise wide-set eyes look out of
 * sockets that did not move. Scaled and shifted about each eye's own centre.
 */
export function bakeEyes(mesh, face) {
  const size = Number(face?.eyeSize) || 0;
  const spacing = Number(face?.eyeSpacing) || 0;
  if (!size && !spacing) return;
  const src = mesh.geometry;
  const p = src?.attributes?.position;
  if (!p) return;
  const arr = readPositions(p);
  /* each eye's own centre, so scaling does not drag it toward the nose */
  let lx = 0,
    ly = 0,
    lz = 0,
    ln = 0,
    rx = 0,
    ry = 0,
    rz = 0,
    rn = 0;
  for (let i = 0; i < p.count; i++) {
    const x = arr[i * 3];
    if (x >= 0) {
      lx += x;
      ly += arr[i * 3 + 1];
      lz += arr[i * 3 + 2];
      ln++;
    } else {
      rx += x;
      ry += arr[i * 3 + 1];
      rz += arr[i * 3 + 2];
      rn++;
    }
  }
  if (ln) {
    lx /= ln;
    ly /= ln;
    lz /= ln;
  }
  if (rn) {
    rx /= rn;
    ry /= rn;
    rz /= rn;
  }
  const s = 1 + 0.16 * size;
  const shift = 0.006 * spacing;
  for (let i = 0; i < p.count; i++) {
    const x = arr[i * 3];
    const cx = x >= 0 ? lx : rx,
      cy = x >= 0 ? ly : ry,
      cz = x >= 0 ? lz : rz;
    const sgn = x >= 0 ? 1 : -1;
    arr[i * 3] = cx + (x - cx) * s + sgn * shift;
    arr[i * 3 + 1] = cy + (arr[i * 3 + 1] - cy) * s;
    arr[i * 3 + 2] = cz + (arr[i * 3 + 2] - cz) * s;
  }
  const geo = new THREE.BufferGeometry();
  if (src.index) geo.setIndex(src.index);
  for (const name of Object.keys(src.attributes)) geo.setAttribute(name, src.attributes[name]);
  geo.setAttribute('position', new THREE.BufferAttribute(arr, 3));
  geo.deleteAttribute('normal');
  geo.computeVertexNormals();
  mesh.geometry = geo;
}

/** Free what an assembled figure owns (its cloned materials); textures are shared and kept. */
export function disposeAssembled(root) {
  root.traverse(o => {
    if (o.isMesh && o.material && o.userData?.kitKind) {
      try {
        o.material.dispose();
      } catch {}
    }
  });
}
